import React, { useContext, useEffect, useRef, useState } from "react";
import { Context } from "../utils/AppContext";
import {
  getOrderDetailById,
  getOrders,
  getOrderStatusList,
  UpdateOrderStatus,
} from "../services/order";
import { ILoginInfo } from "../models/ILoginInfo";
import { IOrders, OrderItem } from "../models/IOrders";
import { decrypt, getMenuExtrasList, parseMenuExtras, getUserToken } from "../utils/Util";
import { IOrderStatusList, OrderStatus } from "../models/IOrderStatusList";
import { getRestaurantById, getRestaurantList, getWorkingHours } from "../services/restaurant";
import { IRestaurantDetail, RestaurantList } from "../models/IRestaurantDetail";
import { IWorkingHour } from "../models/IWorkingHour";
import { IOrderDetail } from "../models/IOrderDetail";
import { IApiError } from "../models/IApiError";
import { applyOrderStatusChange, hasOrder } from "../utils/orderStatusFlow";
import { useOrderHub } from "../hooks/useOrderHub";
import OrderHubStatusBadge from "../components/OrderHubStatusBadge";
import { PrintReceipt } from "../components/PrintReceipt";
import OpenCloseRestaurantComponent from "../components/OpenCloseRestaurant";
import { PromoCodePopup } from "../components/PromoCodePopup";
import LiveDirectionsMap from "../components/LiveDirectionsMap";
import { RiskBadge } from "../incident-module/components/IncidentBadges";

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

const getTodayDow = () => { const d = new Date().getDay(); return d === 0 ? 7 : d; };
const toHHmm = (t: string) => t?.slice(0, 5) ?? "";

function AdminOrderManagementPage() {
  const context = useContext(Context);

  // Restaurant selector
  const [restaurantList, setRestaurantList]   = useState<RestaurantList>();
  const [selectedId, setSelectedId]           = useState("");
  const [restaurantDetail, setRestaurantDetail] = useState<IRestaurantDetail>();

  // Orders & status
  const [todayHour, setTodayHour]           = useState<IWorkingHour | null | undefined>(undefined);
  const [orders, setOrders]                 = useState<IOrders | null>(null);
  const [orderStatusList, setOrderStatusListdata] = useState<IOrderStatusList>();
  const [orderDetail, setorderDetail]       = useState<IOrderDetail>();

  // UI state
  const [mapModal, setMapModal]             = useState<{ lat: number; lng: number; zoom: number } | null>(null);
  const [mapLinkCopied, setMapLinkCopied]   = useState(false);
  const [gettingDirections, setGettingDirections] = useState(false);
  const [directionsError, setDirectionsError]     = useState("");
  const [directionsOrigin, setDirectionsOrigin]   = useState<{ lat: number; lng: number } | null>(null);
  const [liveNav, setLiveNav]                     = useState(false);
  const [collapsedOrders, setCollapsedOrders] = useState<Set<string>>(new Set());
  const [cancelConfirm, setCancelConfirm]     = useState<{ orderId: string; statusId: string } | null>(null);
  const [promoPopupId, setPromoPopupId]       = useState<string | null>(null);
  const [mobileTab, setMobileTab]           = useState<"pending" | "inprogress">("pending");

  // Admin's own userId (needed for the orders API) — read once, stable ref
  const adminUserIdRef = useRef("");

  const toggleCollapse = (id: string) => setCollapsedOrders(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleGetDirections = (): void => {
    if (!mapModal) return;
    if (!navigator.geolocation) {
      setDirectionsError("Tarayıcınız konum özelliğini desteklemiyor.");
      return;
    }
    setDirectionsError("");
    setGettingDirections(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDirectionsOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGettingDirections(false);
      },
      (err) => {
        setGettingDirections(false);
        setDirectionsError(
          err.code === err.PERMISSION_DENIED
            ? "Konum izni reddedildi. Yol tarifi için tarayıcı ayarlarından konuma izin verin."
            : "Konumunuz alınamadı. Lütfen tekrar deneyin."
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const openExternalDirections = (): void => {
    if (!mapModal || !directionsOrigin) return;
    const url = `https://www.google.com/maps/dir/?api=1&origin=${directionsOrigin.lat},${directionsOrigin.lng}&destination=${mapModal.lat},${mapModal.lng}&travelmode=driving`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // SignalR
  const jwtToken = getUserToken()?.token ?? null;
  const hubStatus = useOrderHub({
    token: jwtToken,
    restaurantId: selectedId || null,
    onNewOrder: () => { if (selectedId) getOrderList(selectedId); },
    // Kurye teslim alıp teslim ettiğinde de gelir; satır yerinde güncellenir. Listede olmayan
    // bir sipariş için (ör. liste henüz yüklenmedi) baştan okunur.
    onOrderStatusChanged: (change) => {
      if (!hasOrder(orders, change.orderId)) { if (selectedId) getOrderList(selectedId); return; }
      setOrders((prev) => applyOrderStatusChange(prev, change, orderStatusList));
    },
    onResync: () => { if (selectedId) getOrderList(selectedId); },
  });

  // Sound
  // S3 adresi 403 dönmeye başladı; ses uygulamanın kendi public klasöründen çalınır.
  const bellSound = `${process.env.PUBLIC_URL}/ring.mp3`;
  const playSound = () => {
    try {
      const audio = new Audio(bellSound);
      audio.play().catch((err) => console.warn("[Zil] Ses çalınamadı:", err?.name ?? err));
    } catch {}
  };

  useEffect(() => {
    if (Array.isArray(orders?.items) && orders?.items.some(i => i.isOrderEnabled && !i.isRestourantAccepted)) {
      playSound();
    }
  }, [orders]);

  // On mount: load restaurant list and status list, read admin userId
  useEffect(() => {
    getRestaurantList().then(res => { if (res.data) setRestaurantList(res.data); });
    getOrderStatusList().then(res => setOrderStatusListdata(res.data)).catch(() => {});
    const userJson = localStorage.getItem("user");
    if (userJson) {
      const u: ILoginInfo = JSON.parse(decrypt(userJson));
      adminUserIdRef.current = u.userId;
    }
  }, []);

  // When restaurant changes: reload orders, restaurant detail, working hours
  useEffect(() => {
    if (!selectedId) {
      setOrders(null);
      setRestaurantDetail(undefined);
      setTodayHour(undefined);
      return;
    }
    getRestaurantById(selectedId).then(res => setRestaurantDetail(res.data)).catch(() => {});
    getWorkingHours(selectedId)
      .then(res => setTodayHour(res.data?.find(h => h.dayOfWeek === getTodayDow()) ?? null))
      .catch(() => setTodayHour(null));
    getOrderList(selectedId);
  }, [selectedId]);

  const getOrderList = async (rId: string): Promise<void> => {
    if (!rId) return;
    try {
      const res = await getOrders(rId);
      const dt = res.data;
      if (Array.isArray(dt) && "code" in (dt as any)[0]) {
        setOrders(null);
        return;
      }
      if (dt && typeof dt === "object" && "items" in dt) {
        const typed = dt as IOrders;
        const detailedItems = await Promise.all(
          typed.items.map(async (item) => {
            if (item.isOrderEnabled) {
              const response = await getOrderDetailById(item.id);
              item.orderDetail = response.data;
            }
            return item;
          })
        );
        setOrders({ ...typed, items: [...detailedItems] });
      }
    } catch {
      setOrders(null);
    }
  };

  const updateOrderClicked = (orderId: string, orderStId: string): void => {
    UpdateOrderStatus(orderId, orderStId)
      .then(() => { if (selectedId) getOrderList(selectedId); })
      .catch(() => {});
  };

  // Derived
  const pendingOrders    = orders?.items.filter(i => i.isOrderEnabled && !i.isRestourantAccepted) ?? [];
  const inProgressOrders = orders?.items.filter(i => i.isOrderEnabled && i.isRestourantAccepted) ?? [];

  const renderPromo = (item: OrderItem) => {
    if (!item.appliedPromoCode) return null;
    const p = item.appliedPromoCode;
    const discountLabel = p.discountType === "Percentage" ? `-%${p.discountValue}` : `-${p.discountValue} TL`;
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 12px", background: "#faf5ff", borderTop: "1px solid #ede9fe" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: ".75rem", color: "#9ca3af", textDecoration: "line-through" }}>
            {item.originalTotalPrice} TL
          </span>
          <button
            onClick={e => { e.stopPropagation(); setPromoPopupId(item.promoCodeId!); }}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 20, border: "none", background: "linear-gradient(135deg,#7c3aed,#6d28d9)", color: "#fff", fontSize: ".7rem", fontWeight: 700, cursor: "pointer" }}
          >
            <i className="bi bi-tag-fill" style={{ fontSize: ".65rem" }}></i>
            {p.code} {discountLabel}
          </button>
        </div>
        <span style={{ fontSize: ".72rem", fontWeight: 700, color: "#7c3aed" }}>
          -{item.discountAmount} TL
        </span>
      </div>
    );
  };

  const renderItemDiscount = (item: OrderItem) => {
    if (!item.itemDiscountTotal || item.itemDiscountTotal <= 0) return null;
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 12px", background: "#fffbeb", borderTop: "1px solid #fde68a" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: ".72rem", fontWeight: 700, color: "#b45309" }}>
          <i className="bi bi-percent"></i> Kampanya İndirimi
        </span>
        <span style={{ fontSize: ".72rem", fontWeight: 700, color: "#b45309" }}>
          -{item.itemDiscountTotal} TL
        </span>
      </div>
    );
  };

  // Render helpers (identical to Dashboard)
  const renderFoodList = (item: OrderItem) => (
    <div className="db-food-list">
      {item.orderDetail?.foodPreOrder ? (
        item.orderDetail.foodPreOrder.map((food, fi) => {
          const extras      = parseMenuExtras(food.menuUpgradeNotes).filter(e => e.quantity > 0);
          const extrasTotal = extras.reduce((s, e) => s + e.total, 0);
          const unitPrice           = food.unitPrice || food.food.foodPrice;
          const discountedUnitPrice = food.discountedUnitPrice ?? unitPrice;
          const hasDiscount         = discountedUnitPrice < unitPrice;
          const basePrice   = food.foodQuantity * discountedUnitPrice;
          const lineTotal   = basePrice + extrasTotal;
          return (
            <div key={fi} className="db-food-item">
              <div className="db-food-item__header">
                <span className="db-food-item__name">
                  <i className="bi bi-egg-fried me-1 text-warning"></i>
                  {food.foodQuantity} × {food.food.foodNameTr}
                  {hasDiscount && (
                    <span style={{ marginLeft: 6, fontSize: ".62rem", fontWeight: 700, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "1px 6px" }}>
                      <i className="bi bi-percent"></i> kampanya
                    </span>
                  )}
                </span>
                <span className="db-food-item__price">
                  {hasDiscount && (
                    <span style={{ color: "#9ca3af", fontWeight: 400, marginRight: 6, fontSize: ".78rem", textDecoration: "line-through" }}>
                      {food.foodQuantity * unitPrice} TL
                    </span>
                  )}
                  {basePrice} TL
                </span>
              </div>
              {extras.length > 0 && (
                <div className="db-extras">
                  <div className="db-extras__title">
                    <i className="bi bi-list-check me-1"></i>Eklemeler
                  </div>
                  {extras.map((extra, i) => {
                    const totalQty = food.foodQuantity * extra.quantity;
                    return (
                      <div key={i} className="db-extra-row">
                        <span>
                          <i className="bi bi-plus-circle me-1" style={{ fontSize: ".7rem", color: "#10b981" }}></i>
                          {extra.name} ×{extra.quantity}
                          {food.foodQuantity > 1 && (
                            <span className="db-extra-qty-warning">→ toplam {totalQty} adet</span>
                          )}
                        </span>
                        <span className="db-extra-price">
                          {extra.total === 0 ? "Ücretsiz" : `${extra.total % 1 === 0 ? extra.total : extra.total.toFixed(2)} TL`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {food.foodOrderNote && (
                <div className="db-food-note">
                  <i className="bi bi-chat-square-text-fill me-1"></i>
                  <strong>Not:</strong> {food.foodOrderNote}
                </div>
              )}
              {extras.length > 0 && (
                <div className="db-subtotal">
                  Ara toplam: {lineTotal % 1 === 0 ? lineTotal : lineTotal.toFixed(2)} TL
                </div>
              )}
            </div>
          );
        })
      ) : (
        <div className="text-center py-3 text-muted" style={{ fontSize: ".85rem" }}>
          <span className="spinner-border spinner-border-sm me-2" role="status" />Yükleniyor...
        </div>
      )}
    </div>
  );

  const renderAddress = (item: OrderItem) => (
    <div className="db-address">
      <div className="db-address__main">
        <i className="bi bi-geo-alt-fill me-1 text-danger"></i>
        <strong>{item.customerAddress.customerAddressDescription}</strong>
        <span className="text-muted ms-1">
          {item.customerAddress.city.cityNameTr}/{item.customerAddress.town.townNameTr}
        </span>
      </div>
      {item.customerAddress.customerAddressNote && (
        <div className="db-address__note">
          <i className="bi bi-info-circle-fill me-1"></i>
          <strong>Adres Tarifi:</strong> {item.customerAddress.customerAddressNote}
        </div>
      )}
      {item.customerAddress.customerAddressLatitude != null && item.customerAddress.customerAddressLatitude !== 0 && (
        <button
          className="db-btn-map"
          onClick={() => { setDirectionsError(""); setDirectionsOrigin(null); setLiveNav(false); setMapModal({ lat: item.customerAddress.customerAddressLatitude, lng: item.customerAddress.customerAddressLongitude, zoom: 16 }); }}
        >
          <i className="bi bi-map-fill"></i> Haritada Gör
        </button>
      )}
    </div>
  );

  return (
    <>
      <div className="db-page">

        {/* Header */}
        <div className="db-header-card">
          <div className="db-header-left" style={{ flex: 1, alignItems: "center" }}>
            {/* Logo */}
            {restaurantDetail?.restourantLogoPath ? (
              <img
                src={"https://mutfacik.s3.eu-central-1.amazonaws.com/" + restaurantDetail.restourantLogoPath}
                className="db-restaurant-logo"
                alt={restaurantDetail.restourantName}
              />
            ) : (
              <div style={{
                width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                background: "linear-gradient(135deg,#e0e7ff,#c7d2fe)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className="bi bi-shop" style={{ fontSize: "1.5rem", color: "#6366f1" }}></i>
              </div>
            )}
            {/* Restaurant selector */}
            <div style={{ flex: 1, maxWidth: 420 }}>
              <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "#6b7280", display: "block", marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                <i className="bi bi-shop me-1"></i>Restoran
              </label>
              <select
                className="form-select"
                style={{ borderRadius: "10px", border: "1.5px solid #e5e7eb", fontWeight: 600, fontSize: "0.95rem", color: "#111827", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                value={selectedId}
                onChange={e => setSelectedId(e.target.value)}
              >
                <option value="" disabled>Restoran seçiniz...</option>
                {restaurantList?.items.map(r => (
                  <option key={r.id} value={r.id}>{r.restourantName}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="db-header-right">
            <div className="db-stats">
              <div className={`db-stat-badge${pendingOrders.length > 0 ? " db-stat-badge--pending-active" : ""}`}>
                <i className="bi bi-bell-fill db-stat-icon"></i>
                <span className="db-stat-count">{pendingOrders.length}</span>
                <span className="db-stat-label">Bekleyen</span>
              </div>
              <div className={`db-stat-badge${inProgressOrders.length > 0 ? " db-stat-badge--inprogress-active" : ""}`}>
                <i className="bi bi-arrow-repeat db-stat-icon"></i>
                <span className="db-stat-count">{inProgressOrders.length}</span>
                <span className="db-stat-label">İşlemde</span>
              </div>
            </div>

            {selectedId && <OpenCloseRestaurantComponent restaurantId={selectedId} />}

            {todayHour !== undefined && selectedId && (
              <div
                className="db-wh-widget"
                title="Çalışma saati"
                style={{
                  background: todayHour ? (todayHour.isEnabled ? "#f0fdf4" : "#fef2f2") : "#f9fafb",
                  border: `1.5px solid ${todayHour ? (todayHour.isEnabled ? "#bbf7d0" : "#fecaca") : "#e5e7eb"}`,
                  cursor: "default",
                }}
              >
                <div className="db-wh-widget__label">
                  <i className="bi bi-clock-fill" style={{ color: "#f59e0b" }}></i>Bugün
                </div>
                {todayHour ? (
                  <>
                    <div className="db-wh-widget__time">
                      {toHHmm(todayHour.openTime)} – {toHHmm(todayHour.closeTime)}
                    </div>
                    <div className="db-wh-widget__status">
                      <span className="db-wh-widget__dot" style={{ background: todayHour.isEnabled ? "#10b981" : "#f87171" }} />
                      <span className="db-wh-widget__status-text" style={{ color: todayHour.isEnabled ? "#059669" : "#dc2626" }}>
                        {todayHour.isEnabled ? "Açık" : "Kapalı"}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="db-wh-widget__empty">Tanımlı değil</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* No restaurant selected */}
        {!selectedId ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: "#9ca3af" }}>
            <i className="bi bi-shop" style={{ fontSize: "3rem", display: "block", marginBottom: "12px" }}></i>
            <span style={{ fontSize: "1rem" }}>Siparişleri görmek için restoran seçin.</span>
          </div>
        ) : (
          <>
            {/* Mobile tab bar */}
            <div className="db-mobile-tabs">
              <button
                className={`db-mobile-tab${mobileTab === "pending" ? " db-mobile-tab--active-pending" : ""}`}
                onClick={() => setMobileTab("pending")}
              >
                <i className={`bi bi-bell-fill${pendingOrders.length > 0 ? " db-mobile-tab-bell" : ""}`}></i>
                Gelen Siparişler
                <span className="db-mobile-tab__badge">{pendingOrders.length}</span>
              </button>
              <button
                className={`db-mobile-tab${mobileTab === "inprogress" ? " db-mobile-tab--active-inprogress" : ""}`}
                onClick={() => setMobileTab("inprogress")}
              >
                <i className="bi bi-arrow-repeat"></i>
                İşleme Alınan
                <span className="db-mobile-tab__badge">{inProgressOrders.length}</span>
              </button>
            </div>

            {/* Board */}
            <div className="db-board">

              {/* ── LEFT: Gelen Siparişler ── */}
              <div className={mobileTab !== "pending" ? "db-col--mobile-hidden" : ""}>
                <div className="db-col-header">
                  <div className={`db-col-icon db-col-icon--pending${pendingOrders.length > 0 ? " db-col-icon--has-orders" : ""}`}>
                    <i className="bi bi-bell-fill"></i>
                  </div>
                  <div>
                    <div className="db-col-title">Gelen Siparişler</div>
                    <div className="db-col-subtitle">Onay bekliyor</div>
                    <OrderHubStatusBadge status={hubStatus} />
                  </div>
                  {pendingOrders.length > 0 && (
                    <div className="db-count-badge db-count-badge--pending">{pendingOrders.length} yeni</div>
                  )}
                </div>

                {pendingOrders.length === 0 ? (
                  <div className="db-empty">
                    <i className="bi bi-inbox"></i>
                    <div className="db-empty__text">Bekleyen sipariş yok</div>
                  </div>
                ) : (
                  <div className="db-order-list">
                    {pendingOrders.map((item) => {
                      const collapsed = collapsedOrders.has(item.id);
                      return (
                        <div key={item.id} className="db-card db-card--pending">
                          <div
                            className={`db-card-header db-card-header--pending${collapsed ? " db-card-header--collapsed" : ""}`}
                            onClick={() => toggleCollapse(item.id)}
                          >
                            <span className="db-order-num db-order-num--pending">#{item.orderNameUniqe}</span>
                            <span className="db-card-addr">
                              <i className="bi bi-geo-alt me-1"></i>
                              {item.customerAddress.city.cityNameTr}/{item.customerAddress.town.townNameTr}
                            </span>
                            <div className="db-card-header__right">
                              <RiskBadge risk={item.customerRisk} />
                              <span className="db-card-price">{item.orderPrice} TL</span>
                              {item.orderDate && (
                                <span className="db-time-badge db-time-badge--pending">{formatTime(item.orderDate)}</span>
                              )}
                              <i className={`bi ${collapsed ? "bi-chevron-down" : "bi-chevron-up"} db-chevron db-chevron--pending`}></i>
                            </div>
                          </div>

                          {!collapsed && (
                            <>
                              {item.orderNote && (
                                <div className="db-order-note">
                                  <i className="bi bi-chat-left-text-fill me-1"></i>
                                  <strong>Sipariş Notu:</strong> {item.orderNote}
                                </div>
                              )}
                              {renderFoodList(item)}
                              {renderItemDiscount(item)}
                              {renderPromo(item)}
                              {item.paymentMethod && (
                                <div className="db-payment-method">
                                  <i className="bi bi-wallet2"></i>
                                  <span>{item.paymentMethod.paymentMethodNameTr}</span>
                                </div>
                              )}
                              <div className="db-total-bar db-total-bar--pending">
                                <span className="db-total-label db-total-label--pending">
                                  <i className="bi bi-receipt me-1"></i>Toplam Tutar
                                </span>
                                <span className="db-total-amount db-total-amount--pending">{item.orderPrice} TL</span>
                              </div>
                              {renderAddress(item)}
                              <div className="db-actions db-actions--pending">
                                <div className="db-btn-grid">
                                  <button
                                    className="db-btn db-btn--accept"
                                    onClick={() => updateOrderClicked(item.id, orderStatusList![2].id)}
                                  >
                                    <i className="bi bi-box2-heart"></i> Hazırlanıyor
                                  </button>
                                  <button
                                    className="db-btn db-btn--cancel-pending"
                                    onClick={() => setCancelConfirm({ orderId: item.id, statusId: orderStatusList![4].id })}
                                  >
                                    <i className="bi bi-x-circle-fill"></i> İptal Et
                                  </button>
                                </div>
                                <PrintReceipt order={item} restaurantName={restaurantDetail?.restourantName ?? ""} />
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── RIGHT: İşleme Alınan ── */}
              <div className={mobileTab !== "inprogress" ? "db-col--mobile-hidden" : ""}>
                <div className="db-col-header">
                  <div className="db-col-icon db-col-icon--inprogress">
                    <i className="bi bi-arrow-repeat"></i>
                  </div>
                  <div>
                    <div className="db-col-title">İşleme Alınan</div>
                    <div className="db-col-subtitle">Hazırlanıyor / Yolda</div>
                  </div>
                  {inProgressOrders.length > 0 && (
                    <div className="db-count-badge db-count-badge--inprogress">{inProgressOrders.length} aktif</div>
                  )}
                </div>

                {inProgressOrders.length === 0 ? (
                  <div className="db-empty">
                    <i className="bi bi-check2-all"></i>
                    <div className="db-empty__text">İşlemde sipariş yok</div>
                  </div>
                ) : (
                  <div className="db-order-list">
                    {inProgressOrders.map((item) => {
                      const collapsed      = collapsedOrders.has(item.id);
                      const isHazirlanıyor = item.orderStatusId === orderStatusList?.[2].id;
                      const isYolda        = item.orderStatusId === orderStatusList?.[3].id;
                      const variant        = isYolda ? "onroad" : isHazirlanıyor ? "preparing" : "other";
                      const statusLabel    = isYolda ? "Yolda" : isHazirlanıyor ? "Hazırlanıyor" : "İşlemde";
                      const statusIcon     = isYolda ? "bi-car-front-fill" : isHazirlanıyor ? "bi-cup-straw" : "bi-arrow-repeat";

                      return (
                        <div key={item.id} className={`db-card db-card--${variant}`}>
                          <div
                            className={`db-card-header db-card-header--${variant}${collapsed ? " db-card-header--collapsed" : ""}`}
                            onClick={() => toggleCollapse(item.id)}
                          >
                            <span className={`db-order-num db-order-num--${variant}`}>#{item.orderNameUniqe}</span>
                            <span className="db-card-addr">
                              <i className="bi bi-geo-alt me-1"></i>
                              {item.customerAddress.city.cityNameTr}/{item.customerAddress.town.townNameTr}
                            </span>
                            <div className="db-card-header__right">
                              <RiskBadge risk={item.customerRisk} />
                              <span className={`db-status-label db-status-label--${variant}`}>
                                <i className={`bi ${statusIcon}`}></i>{statusLabel}
                              </span>
                              <span className="db-card-price db-card-price--neutral">{item.orderPrice} TL</span>
                              {item.orderDate && (
                                <span className="db-time-badge db-time-badge--inprogress">{formatTime(item.orderDate)}</span>
                              )}
                              <i className={`bi ${collapsed ? "bi-chevron-down" : "bi-chevron-up"} db-chevron db-chevron--${variant}`}></i>
                            </div>
                          </div>

                          {!collapsed && (
                            <>
                              {item.orderNote && (
                                <div className="db-order-note">
                                  <i className="bi bi-chat-left-text-fill me-1"></i>
                                  <strong>Sipariş Notu:</strong> {item.orderNote}
                                </div>
                              )}
                              {renderFoodList(item)}
                              {renderItemDiscount(item)}
                              {renderPromo(item)}
                              {item.paymentMethod && (
                                <div className="db-payment-method">
                                  <i className="bi bi-wallet2"></i>
                                  <span>{item.paymentMethod.paymentMethodNameTr}</span>
                                </div>
                              )}
                              <div className={`db-total-bar db-total-bar--${variant}`}>
                                <span className={`db-total-label db-total-label--${variant}`}>
                                  <i className="bi bi-receipt me-1"></i>Toplam Tutar
                                </span>
                                <span className={`db-total-amount db-total-amount--${variant}`}>
                                  {item.orderDetail?.orderPrice ?? item.orderPrice}{" "}
                                  {item.orderDetail?.currency?.currencyDisplayText ?? "TL"}
                                </span>
                              </div>
                              {renderAddress(item)}
                              {(isHazirlanıyor || isYolda) && item.preOrder?.customer && (
                                <div className="db-customer-bar">
                                  <div className="db-customer-bar__item">
                                    <i className="bi bi-person-fill"></i>
                                    <span>{item.preOrder.customer.customerName} {item.preOrder.customer.customerSurName}</span>
                                  </div>
                                  {item.preOrder.customer.customerTelNo && (
                                    <div className="db-customer-bar__item">
                                      <i className="bi bi-telephone-fill"></i>
                                      <span>{item.preOrder.customer.customerTelNo}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className={`db-actions db-actions--${variant}`}>
                                <div className="db-btn-grid">
                                  <button
                                    className="db-btn db-btn--preparing"
                                    onClick={() => updateOrderClicked(item.id, orderStatusList![2].id)}
                                  >
                                    <i className="bi bi-cup-straw"></i> Hazırlanıyor
                                  </button>
                                  <button
                                    className={`db-btn ${isYolda ? "db-btn--onroad-active" : "db-btn--onroad"}`}
                                    onClick={() => updateOrderClicked(item.id, orderStatusList![3].id)}
                                  >
                                    <i className="bi bi-car-front-fill"></i> Yola Çıktı
                                  </button>
                                  <button
                                    className="db-btn db-btn--deliver"
                                    onClick={() => updateOrderClicked(item.id, orderStatusList![1].id)}
                                  >
                                    <i className="bi bi-check-circle-fill"></i> Teslim Edildi
                                  </button>
                                  <button
                                    className="db-btn db-btn--cancel"
                                    onClick={() => setCancelConfirm({ orderId: item.id, statusId: orderStatusList![4].id })}
                                  >
                                    <i className="bi bi-x-circle-fill"></i> İptal Et
                                  </button>
                                </div>
                                <PrintReceipt order={item} restaurantName={restaurantDetail?.restourantName ?? ""} />
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          </>
        )}
      </div>

      {/* Google Maps Modal */}
      {mapModal && (
        <div
          onClick={() => setMapModal(null)}
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.65)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "14px", overflow: "hidden", width: "92%", maxWidth: "640px", boxShadow: "0 8px 40px rgba(0,0,0,0.35)" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ fontWeight: 700, fontSize: "1rem" }}>
                    <i className="bi bi-map-fill me-2 text-primary"></i>Konum
                  </span>
                  {!directionsOrigin && (
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", background: "#f1f5f9", borderRadius: "8px", padding: "2px 6px" }}>
                      <button onClick={() => setMapModal(prev => prev ? { ...prev, zoom: Math.max(1, prev.zoom - 1) } : prev)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.1rem", color: "#374151", fontWeight: 700, lineHeight: 1, padding: "2px 6px" }}>−</button>
                      <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#6b7280", minWidth: "24px", textAlign: "center" }}>{mapModal?.zoom}</span>
                      <button onClick={() => setMapModal(prev => prev ? { ...prev, zoom: Math.min(21, prev.zoom + 1) } : prev)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.1rem", color: "#374151", fontWeight: 700, lineHeight: 1, padding: "2px 6px" }}>+</button>
                    </div>
                  )}
                </div>
                <button onClick={() => setMapModal(null)} style={{ background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer", color: "#9ca3af", lineHeight: 1, padding: "0 4px" }}>&times;</button>
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", marginTop: "10px" }}>
                {!directionsOrigin ? (
                  <button
                    onClick={handleGetDirections}
                    disabled={gettingDirections}
                    style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "0.85rem", color: "#fff", background: "#ea580c", borderRadius: "7px", padding: "5px 13px", border: "none", cursor: gettingDirections ? "not-allowed" : "pointer", fontWeight: 600, opacity: gettingDirections ? 0.75 : 1 }}
                  >
                    {gettingDirections ? (
                      <><span className="spinner-border spinner-border-sm" style={{ width: "12px", height: "12px" }}></span> Konum alınıyor...</>
                    ) : (
                      <><i className="bi bi-signpost-split-fill"></i> Yol Tarifi Al</>
                    )}
                  </button>
                ) : !liveNav ? (
                  <>
                    <button
                      onClick={() => setLiveNav(true)}
                      style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "0.85rem", color: "#fff", background: "#ea580c", borderRadius: "7px", padding: "5px 13px", border: "none", cursor: "pointer", fontWeight: 600 }}
                    >
                      <i className="bi bi-play-fill"></i> Navigasyonu Başlat
                    </button>
                    <button
                      onClick={() => { setDirectionsOrigin(null); setLiveNav(false); }}
                      title="Yer görünümüne dön"
                      style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "0.85rem", color: "#374151", background: "#f1f5f9", borderRadius: "7px", padding: "5px 13px", border: "none", cursor: "pointer", fontWeight: 600 }}
                    >
                      <i className="bi bi-arrow-counterclockwise"></i> Yer Görünümü
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={openExternalDirections}
                      title="Sesli, dönüş dönüş navigasyon için Google Maps uygulamasını açar"
                      style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "0.85rem", color: "#fff", background: "#1d4ed8", borderRadius: "7px", padding: "5px 13px", border: "none", cursor: "pointer", fontWeight: 600 }}
                    >
                      <i className="bi bi-mic-fill"></i> Sesli Navigasyon
                    </button>
                    <button
                      onClick={() => { setDirectionsOrigin(null); setLiveNav(false); }}
                      title="Yer görünümüne dön"
                      style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "0.85rem", color: "#374151", background: "#f1f5f9", borderRadius: "7px", padding: "5px 13px", border: "none", cursor: "pointer", fontWeight: 600 }}
                    >
                      <i className="bi bi-arrow-counterclockwise"></i> Yer Görünümü
                    </button>
                  </>
                )}
                <button
                  onClick={() => { const link = `https://www.google.com/maps?q=${mapModal.lat},${mapModal.lng}`; navigator.clipboard.writeText(link).then(() => { setMapLinkCopied(true); setTimeout(() => setMapLinkCopied(false), 2000); }); }}
                  style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "0.85rem", color: "#fff", background: mapLinkCopied ? "#16a34a" : "#059669", borderRadius: "7px", padding: "5px 13px", border: "none", cursor: "pointer", fontWeight: 600, transition: "background 0.2s" }}
                >
                  <i className={`bi ${mapLinkCopied ? "bi-check-lg" : "bi-clipboard-fill"}`}></i>
                  {mapLinkCopied ? "Kopyalandı!" : "Linki Kopyala"}
                </button>
                <a href={`https://www.google.com/maps?q=${mapModal.lat},${mapModal.lng}`} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "0.85rem", color: "#fff", background: "#1d4ed8", borderRadius: "7px", padding: "5px 13px", textDecoration: "none", fontWeight: 600 }}>
                  <i className="bi bi-box-arrow-up-right"></i> Maps'te Aç
                </a>
              </div>
              {directionsError && (
                <div style={{ marginTop: "8px", fontSize: "0.8rem", color: "#b91c1c", background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: "8px", padding: "6px 10px" }}>
                  <i className="bi bi-exclamation-circle me-1"></i>{directionsError}
                </div>
              )}
            </div>
            {liveNav && directionsOrigin ? (
              <LiveDirectionsMap origin={directionsOrigin} destination={{ lat: mapModal.lat, lng: mapModal.lng }} />
            ) : (
              <iframe
                title="Google Maps"
                width="100%" height="420"
                style={{ border: 0, display: "block" }}
                loading="lazy" allowFullScreen
                key={directionsOrigin ? `dir-${directionsOrigin.lat}-${directionsOrigin.lng}` : `place-${mapModal.zoom}`}
                src={
                  directionsOrigin
                    ? `https://www.google.com/maps/embed/v1/directions?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&origin=${directionsOrigin.lat},${directionsOrigin.lng}&destination=${mapModal.lat},${mapModal.lng}&mode=driving`
                    : `https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${mapModal.lat},${mapModal.lng}&zoom=${mapModal.zoom}`
                }
              />
            )}
          </div>
        </div>
      )}

      {/* Promo Code Popup */}
      <PromoCodePopup promoCodeId={promoPopupId} onClose={() => setPromoPopupId(null)} />

      {/* İptal Onay Popup */}
      {cancelConfirm && (
        <>
          <div onClick={() => setCancelConfirm(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050 }} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", background: "#fff", borderRadius: "16px", zIndex: 1051, padding: "28px 32px", minWidth: "320px", maxWidth: "90vw", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", textAlign: "center" }}>
            <div style={{ fontSize: "2.5rem", marginBottom: "8px" }}>
              <i className="bi bi-x-circle-fill" style={{ color: "#ef4444" }}></i>
            </div>
            <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#111827", marginBottom: "6px" }}>Siparişi İptal Et</div>
            <div style={{ fontSize: "0.88rem", color: "#6b7280", marginBottom: "24px" }}>Bu siparişi iptal etmek istediğinizden emin misiniz?</div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button onClick={() => setCancelConfirm(null)} style={{ padding: "9px 22px", borderRadius: "10px", border: "1.5px solid #e5e7eb", background: "#f9fafb", color: "#374151", fontWeight: 600, fontSize: "0.9rem", cursor: "pointer" }}>Hayır</button>
              <button
                onClick={() => { updateOrderClicked(cancelConfirm.orderId, cancelConfirm.statusId); setCancelConfirm(null); }}
                style={{ padding: "9px 22px", borderRadius: "10px", border: "none", background: "#ef4444", color: "#fff", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer" }}
              >
                <i className="bi bi-x-circle-fill me-1"></i> Evet, İptal Et
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default AdminOrderManagementPage;
